/**
 * useGeoEvidence — the customer-side live-location capture machine (S5-9).
 *
 * Runs BEFORE the media join on the call screen (the transport stays disabled
 * until this resolves), with permission priming: the browser's geolocation
 * prompt fires only from the explainer's explicit button, never as an ambush
 * on page load.
 *
 * Honesty rules encoded here:
 *  • A denial/timeout/unsupported device still POSTS — an honest MISSING
 *    capture (permissionState says why, no coordinates) so the officer sees
 *    "location evidence missing", and the pack's denial handling applies
 *    server-side. Nothing is ever fabricated client-side.
 *  • Denial NEVER silently blocks the call — the customer reads honest copy
 *    and continues; enforcement is the officer's/gate's (never a dead end).
 *  • Mock mode (no backend) resolves immediately WITHOUT prompting: no
 *    evidence rail exists, so no location is collected (data minimisation).
 *  • A 409 from the rail means evidence already exists (a reload/rejoin):
 *    resolve quietly if this attempt carried no fix; append explicitly
 *    (recapture) if the customer just granted a real fix worth recording.
 *
 * TWO MOMENTS, NOT ONE (KYCID·00001-00006-00002 AC13). The customer's position
 * is captured at JOIN and again at the START OF THE LIVE LEG, on the same
 * append-only attempt ladder. Only the live-leg half existed before: a customer
 * who never reached the call screen left NO geo evidence at all — and therefore
 * no source IP either, since the server derives it from the request that
 * carries the capture. That is what `mode` selects:
 *
 *   'gate'       — the call screen. Blocks the media join until resolved, with
 *                  permission priming (the browser prompt fires only from the
 *                  explainer's button, never as an ambush).
 *   'background' — the join moment. Fires ONCE on mount and never blocks: the
 *                  customer is waiting in the queue, and holding them there for
 *                  a location prompt would be a worse experience than asking on
 *                  the call screen where it actually matters.
 *
 * Why background mode still posts when the fix fails: the source IP, its ASN,
 * country and VPN signal are derived SERVER-SIDE from the capture request. So a
 * DENIED/TIMEOUT post is not an empty gesture — it is how the customer's network
 * evidence gets recorded at all. Refusing to post because there is no GPS fix
 * would discard the IP evidence too.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/api';
import { postGeoEvidence, type GeoCaptureBody, type GeoPermissionState } from '@/api/geo';

/** How long we wait for a fix before honestly recording TIMEOUT. */
const FIX_TIMEOUT_MS = 15_000;

/**
 * 13-Aug #36: one bounded retry before settling in post-failed. A single transient failure at the
 * join moment (network blip, or the consent write landing a beat after this fires) must not be the
 * difference between geo evidence existing and a silent blank — especially in background mode,
 * where nothing on screen invites a manual retry. A refused POST wrote nothing server-side, so the
 * retry is safe; exactly one, so a genuine outage still resolves to the honest failed state fast.
 */
const POST_RETRY_DELAY_MS = 2_500;

export type GeoGatePhase =
  | 'explain' // priming card — the browser prompt has NOT fired yet
  | 'requesting' // browser prompt / fix acquisition in flight
  | 'posting' // fix (or honest missing state) being recorded
  | 'captured' // recorded — resolve to the call
  | 'denied' // permission denied; honest copy + continue
  | 'degraded' // unsupported / timeout / error; honest copy + retry/continue
  | 'post-failed' // capture ok but the record POST failed; retry/continue
  | 'resolved'; // gate finished (captured or explicitly continued)

export interface GeoGateState {
  readonly phase: GeoGatePhase;
  /** True once the gate no longer blocks the join. */
  readonly resolved: boolean;
  /** Calm, customer-facing detail line for degrade states (never a stack trace). */
  readonly detail: string | null;
  /** Fire the priming CTA — requests the browser permission then records. */
  readonly share: () => void;
  /** Retry after a degrade/denial (re-requests; appends a re-capture if needed). */
  readonly retry: () => void;
  /** Continue without location — the officer will see evidence is missing. */
  readonly continueWithout: () => void;
}

/** Which moment this instance serves — see the header block. */
export type GeoCaptureMode = 'gate' | 'background';

export function useGeoEvidence(
  sessionId: string,
  enabled: boolean,
  mode: GeoCaptureMode = 'gate',
): GeoGateState {
  const [phase, setPhase] = useState<GeoGatePhase>('explain');
  const [detail, setDetail] = useState<string | null>(null);
  // True once ANY row for this (session, CUSTOMER) exists — later posts must be explicit re-captures.
  const recordedOnce = useRef(false);
  const disposed = useRef(false);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  // Demo mode / no session: nothing to bind evidence to — resolve, prompt nothing.
  useEffect(() => {
    if (!enabled) setPhase('resolved');
  }, [enabled]);

  const record = useCallback(
    async (body: GeoCaptureBody, hadFix: boolean, attempt = 0) => {
      setPhase('posting');
      try {
        await postGeoEvidence(sessionId, { ...body, recapture: recordedOnce.current });
        recordedOnce.current = true;
        if (disposed.current) return;
        if (hadFix) {
          setPhase('captured');
          window.setTimeout(() => {
            if (!disposed.current) setPhase('resolved');
          }, 900); // brief confirmation beat, then the call
        } else if (body.permissionState === 'DENIED') {
          setPhase('denied');
        } else {
          setPhase('degraded');
        }
      } catch (e) {
        if (disposed.current) return;
        if (e instanceof ApiError && e.status === 409) {
          // Evidence already exists for this session (reload/rejoin). If we were
          // only recording a missing-state, the existing row already says more —
          // resolve quietly. A real fix is worth appending: retry as re-capture.
          recordedOnce.current = true;
          if (hadFix) {
            try {
              await postGeoEvidence(sessionId, { ...body, recapture: true });
              if (!disposed.current) setPhase('resolved');
              return;
            } catch {
              /* fall through to the honest post-failed state */
            }
          } else {
            setPhase(body.permissionState === 'DENIED' ? 'denied' : 'resolved');
            return;
          }
        } else if (attempt === 0) {
          // 13-Aug #36: one bounded retry before the honest failed state — see POST_RETRY_DELAY_MS.
          // Not for 409 (already handled above: that IS a second, explicit re-capture attempt).
          window.setTimeout(() => {
            if (!disposed.current) void record(body, hadFix, 1);
          }, POST_RETRY_DELAY_MS);
          return; // phase stays 'posting' — the retry is part of the same recording act
        }
        setDetail('Your location could not be recorded right now.');
        setPhase('post-failed');
      }
    },
    [sessionId],
  );

  const acquire = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setDetail("This device doesn't support location.");
      void record({ permissionState: 'UNSUPPORTED' }, false);
      return;
    }
    setPhase('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (disposed.current) return;
        // Non-standard Android-WebView hint; browsers cannot detect OS mock
        // location — null is the honest "not detectable" default.
        const rawMocked = (pos.coords as GeolocationCoordinates & { mocked?: unknown }).mocked;
        void record(
          {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            accuracyM: pos.coords.accuracy,
            capturedAtMs: pos.timestamp,
            permissionState: 'GRANTED',
            clientHints: { mocked: typeof rawMocked === 'boolean' ? rawMocked : null },
          },
          true,
        );
      },
      (err) => {
        if (disposed.current) return;
        let state: GeoPermissionState = 'ERROR';
        if (err.code === err.PERMISSION_DENIED) {
          state = 'DENIED';
          setDetail(null);
        } else if (err.code === err.TIMEOUT) {
          state = 'TIMEOUT';
          setDetail("We couldn't get a location fix in time.");
        } else {
          setDetail('Location is unavailable on this device right now.');
        }
        void record({ permissionState: state }, false);
      },
      { enableHighAccuracy: true, timeout: FIX_TIMEOUT_MS, maximumAge: 30_000 },
    );
  }, [record]);

  // BACKGROUND mode (AC13's join-moment half): fire exactly once, unprompted, and never block.
  // `firedRef` — not a phase check — is the guard, because the phase legitimately moves through
  // several values and a phase-based guard would re-fire on each transition.
  const firedRef = useRef(false);
  useEffect(() => {
    if (mode !== 'background' || !enabled || firedRef.current) return;
    firedRef.current = true;
    acquire();
  }, [mode, enabled, acquire]);

  const continueWithout = useCallback(() => {
    setPhase('resolved');
  }, []);

  return {
    phase,
    // Background mode never gates anything — the queue must not stall on a location prompt.
    resolved: mode === 'background' ? true : phase === 'resolved',
    detail,
    share: acquire,
    retry: acquire,
    continueWithout,
  };
}
